import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Lock, BookOpen, Search } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";

const QUALS = [
  { slug: "nebosh-igc", name: "NEBOSH IGC" },
  { slug: "nvq-5", name: "NVQ Level 5" },
  { slug: "nvq-6", name: "NVQ Level 6" },
  { slug: "nvq-7", name: "NVQ Level 7" },
  { slug: "othm-5", name: "OTHM Level 5" },
  { slug: "othm-6", name: "OTHM Level 6" },
  { slug: "othm-7", name: "OTHM Level 7" },
  { slug: "qualifi-diploma", name: "Qualifi Diploma" },
];

export default function Learn() {
  const [tab, setTab] = useState("library");
  const [cats, setCats] = useState([]);
  const [topics, setTopics] = useState([]);
  const [cat, setCat] = useState("all");
  const [q, setQ] = useState("");
  const [qual, setQual] = useState(QUALS[0].slug);
  const [search] = useSearchParams();

  useEffect(() => { api.get("/library/categories").then(r => setCats(r.data.categories)); }, []);

  useEffect(() => {
    if (tab === "library") {
      const params = {};
      if (cat !== "all") params.category = cat;
      if (q) params.q = q;
      api.get("/library/topics", { params }).then(r => setTopics(r.data.topics));
    } else {
      api.get("/library/topics", { params: { qualification: qual } }).then(r => setTopics(r.data.topics));
    }
  }, [tab, cat, q, qual]);

  const TopicCard = ({ t }) => (
    <Link to={`/learn/topic/${t.slug}`} data-testid={`topic-card-${t.slug}`}
          className={`block rounded-2xl border p-5 card-hover ${t.locked ? "border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50" : "border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 hover:border-amber-500"}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500">
            <BookOpen className="h-3 w-3" /> {t.category_slug || t.qualification}
          </div>
          <h3 className="mt-1 text-base font-semibold truncate">{t.title}</h3>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400 line-clamp-2">{t.summary}</p>
        </div>
        {t.locked && <Lock className="h-4 w-4 text-amber-500 flex-shrink-0" />}
      </div>
      <div className="mt-3 flex items-center justify-between text-xs">
        <span className="text-slate-500">{t.reading_time || 6} min read</span>
        {t.locked ? <span className="text-amber-600 font-semibold">Premium</span> : <span className="text-emerald-500 font-semibold">Free</span>}
      </div>
    </Link>
  );

  return (
    <div className="space-y-5" data-testid="learn-page">
      <div>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Learn</h1>
        <p className="text-slate-600 dark:text-slate-400 mt-1">Digital library, qualification prep and quick references.</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid grid-cols-2 max-w-md">
          <TabsTrigger data-testid="learn-tab-library" value="library">Library</TabsTrigger>
          <TabsTrigger data-testid="learn-tab-qualifications" value="quals">Qualifications</TabsTrigger>
        </TabsList>

        <TabsContent value="library" className="mt-4 space-y-4">
          <div className="flex flex-col md:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input data-testid="library-search" value={q} onChange={(e)=>setQ(e.target.value)} placeholder="Search topics…" className="pl-9 h-11" />
            </div>
            <div className="flex gap-2 overflow-x-auto pb-1">
              <button onClick={()=>setCat("all")} data-testid="filter-cat-all" className={`pill ${cat === "all" ? "bg-slate-900 text-white dark:bg-amber-500 dark:text-slate-950" : "bg-slate-100 dark:bg-slate-800"}`}>All</button>
              {cats.map(c => (
                <button key={c.slug} data-testid={`filter-cat-${c.slug}`} onClick={()=>setCat(c.slug)}
                        className={`pill whitespace-nowrap ${cat === c.slug ? "bg-slate-900 text-white dark:bg-amber-500 dark:text-slate-950" : "bg-slate-100 dark:bg-slate-800"}`}>{c.name}</button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {topics.filter(t => (t.category_slug !== "qualification")).map(t => <TopicCard key={t.topic_id} t={t} />)}
          </div>
        </TabsContent>

        <TabsContent value="quals" className="mt-4 space-y-4">
          <div className="flex gap-2 overflow-x-auto pb-1">
            {QUALS.map(q => (
              <button key={q.slug} data-testid={`qual-${q.slug}`} onClick={()=>setQual(q.slug)}
                      className={`pill whitespace-nowrap ${qual === q.slug ? "bg-slate-900 text-white dark:bg-amber-500 dark:text-slate-950" : "bg-slate-100 dark:bg-slate-800"}`}>{q.name}</button>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {topics.map(t => <TopicCard key={t.topic_id} t={t} />)}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
